import React, { useEffect, useState } from "react";
import { Plus, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import AdminLayout from "../../components/admin/AdminLayout";
import AdminUsersTable from "../../components/admin/AdminUsersTable";

// The signed-in admin, so the table can flag "You" and block self-delete.
const currentUser = (() => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch (e) {
    return null;
  }
})();

const emptyForm = { name: "", email: "", password: "", confirm_password: "" };

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add/Edit dialog state. `editing` holds the user being edited, or null for add.
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Toggle plain-text for the password fields.
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // The user pending deletion (drives the confirm dialog).
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/users");
      // Be tolerant of different response shapes (array, {data}, {users}).
      const list = Array.isArray(data) ? data : data?.data || data?.users || [];
      setUsers(list);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const resetFormState = () => {
    setErrors({});
    setShowPassword(false);
    setShowConfirm(false);
  };

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    resetFormState();
    setFormOpen(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    // Password stays blank on edit — only sent if the admin types a new one.
    setForm({
      name: user.name || "",
      email: user.email || "",
      password: "",
      confirm_password: "",
    });
    resetFormState();
    setFormOpen(true);
  };

  // Validate a single field; returns an error string ("" when valid).
  // `state` is the form snapshot to read the sibling password from.
  const validateField = (name, value, state = form) => {
    const v = (value || "").trim();
    switch (name) {
      case "name":
        if (!v) return "Name is required.";
        if (v.length < 2) return "Name must be at least 2 characters.";
        if (v.length > 35) return "Name must be under 35 characters.";
        if (!/^[a-zA-Z\s.'-]+$/.test(v))
          return "Name can only contain letters.";
        return "";
      case "email":
        if (!v) return "Email address is required.";
        if (v.length > 100) return "Email must be under 100 characters.";
        if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(v))
          return "Enter a valid email address.";
        return "";
      case "password":
        // Optional on edit (blank keeps the current password); required on add.
        if (!v) return editing ? "" : "Password is required.";
        if (v.length < 8) return "Password must be at least 8 characters.";
        return "";
      case "confirm_password":
        // Only relevant when a password is being set.
        if (!state.password) return "";
        if (!v) return "Please confirm the password.";
        if (v !== state.password) return "Passwords do not match.";
        return "";
      default:
        return "";
    }
  };

  const validateAll = () => {
    const nextErrors = {};
    // New users get a default password from the backend, so password fields
    // are only validated when editing an existing user.
    const keys = editing
      ? ["name", "email", "password", "confirm_password"]
      : ["name", "email"];
    keys.forEach((key) => {
      const error = validateField(key, form[key], form);
      if (error) nextErrors[key] = error;
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // Update a field and re-validate it live (mirrors the Contact Us page).
  const setField = (key) => (value) => {
    const next = { ...form, [key]: value };
    setForm(next);
    setErrors((prev) => {
      const e = { ...prev, [key]: validateField(key, value, next) };
      // Keep the confirm error in sync when the password itself changes.
      if (key === "password") {
        e.confirm_password = validateField(
          "confirm_password",
          next.confirm_password,
          next
        );
      }
      return e;
    });
  };

  // Create or update, depending on whether we're editing.
  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateAll()) {
      toast.error(
        "We identified an issue with the information you provided. Please review the form again."
      );
      return;
    }
    setSaving(true);

    try {
      if (editing) {
        // Only send the password when the admin entered a new one.
        const payload = {
          name: form.name,
          email: form.email,
        };
        if (form.password.trim()) payload.password = form.password;

        const { data } = await api.post(`/users/${editing.id}/update`, payload);
        const updated = data?.data || { ...editing, ...payload };
        setUsers((prev) =>
          prev.map((u) => (u.id === editing.id ? { ...u, ...updated } : u))
        );
        toast.success("User updated");
      } else {
        // No password sent — the backend assigns a default password.
        const { data } = await api.post("/users/add", {
          name: form.name,
          email: form.email,
        });
        const created = data?.data;
        if (created) {
          setUsers((prev) => [created, ...prev]);
        } else {
          fetchUsers();
        }
        toast.success("User created");
      }
      setFormOpen(false);
    } catch (error) {
      // Surface the first Laravel validation error if present.
      const errors = error.response?.data?.errors;
      const firstError = errors && Object.values(errors)?.[0]?.[0];
      toast.error(
        firstError ||
          error.response?.data?.message ||
          "Failed to save user"
      );
    } finally {
      setSaving(false);
    }
  };

  // Confirm + delete via the API.
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.post(`/users/${deleteTarget.id}/delete`);
      setUsers((prev) => prev.filter((u) => u.id !== deleteTarget.id));
      toast.success("User deleted");
      setDeleteTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout
      title="Users"
      actions={
        <Button
          onClick={openAdd}
          className="gap-2 bg-[#b32126] hover:bg-[#8f1a1f]"
        >
          <Plus className="h-4 w-4" />
          Add User
        </Button>
      }
    >
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Users</h2>
        <p className="mt-1 text-sm text-gray-600">
          Manage admin panel users and their access.
        </p>
      </div>

      <AdminUsersTable
        users={users}
        loading={loading}
        currentUserId={currentUser?.id}
        onEdit={openEdit}
        onDelete={(user) => setDeleteTarget(user)}
      />

      {/* Add / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={(open) => !saving && setFormOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit User" : "Add User"}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="user-name">Name</Label>
              <Input
                id="user-name"
                value={form.name}
                onChange={(e) => setField("name")(e.target.value)}
                placeholder="Full name"
                className={errors.name ? "border-red-400" : ""}
              />
              {errors.name && (
                <p className="text-xs text-red-500">{errors.name}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="user-email">Email</Label>
              <Input
                id="user-email"
                type="email"
                value={form.email}
                onChange={(e) => setField("email")(e.target.value)}
                placeholder="name@example.com"
                className={errors.email ? "border-red-400" : ""}
              />
              {errors.email && (
                <p className="text-xs text-red-500">{errors.email}</p>
              )}
            </div>

            {/* Password fields are only for editing — new users are created
                with a default password assigned by the backend. */}
            {editing ? (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="user-password">
                    Password{" "}
                    <span className="text-xs font-normal text-gray-400">
                      (leave blank to keep current)
                    </span>
                  </Label>
                  <div className="relative">
                    <Input
                      id="user-password"
                      type={showPassword ? "text" : "password"}
                      value={form.password}
                      onChange={(e) => setField("password")(e.target.value)}
                      placeholder="••••••••"
                      className={`pr-10 ${
                        errors.password ? "border-red-400" : ""
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      title={showPassword ? "Hide password" : "Show password"}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-xs text-red-500">{errors.password}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="user-confirm-password">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="user-confirm-password"
                      type={showConfirm ? "text" : "password"}
                      value={form.confirm_password}
                      onChange={(e) =>
                        setField("confirm_password")(e.target.value)
                      }
                      placeholder="Re-enter password"
                      className={`pr-10 ${
                        errors.confirm_password ? "border-red-400" : ""
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      title={showConfirm ? "Hide password" : "Show password"}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirm ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {errors.confirm_password && (
                    <p className="text-xs text-red-500">
                      {errors.confirm_password}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <p className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-500">
                A default password will be assigned. The user can change it
                after logging in.
              </p>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setFormOpen(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#b32126] hover:bg-[#8f1a1f]"
              >
                {saving
                  ? "Saving…"
                  : editing
                  ? "Save Changes"
                  : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete{" "}
              <span className="font-medium text-gray-900">
                {deleteTarget?.name || deleteTarget?.email || "this user"}
              </span>
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                confirmDelete();
              }}
              disabled={deleting}
              className="bg-[#b32126] hover:bg-[#8f1a1f]"
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
};

export default AdminUsers;
